import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from 'react-leaflet';
import { Maximize2, Minimize2, LocateFixed } from 'lucide-react';
import { ReactComponent as UsersIcon } from '../../../../assets/icons/users-icon.svg';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ColorModeContext } from '../../../../App';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@luomus/leaflet-smooth-wheel-zoom';

// Fix for default marker icons in Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

interface CityData {
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  uniqueUsers: number;
  events: number;
}

interface CitiesMapProps {
  data: CityData[];
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  loading?: boolean;
}

// Custom marker component with elegant shining aura effect
const AnimatedCityMarker: React.FC<{
  city: CityData;
  index: number;
}> = ({ city, index }) => {
  const { t } = useTranslation();
  // Simple static marker - optimized for performance with many markers
  const dotSize = 8;
  const containerSize = 16;

  // Create custom icon - static, no animations.
  // The dot's own styling lives in index.css under `.cities-map .marker-core`
  // — leaflet injects this HTML outside React, and repeating a <style> block
  // per marker (as this did) meant one copy of the rules per city.
  const createCustomIcon = () => {
    const iconHtml = `
      <div class="static-marker-container" style="
        position: relative;
        width: ${containerSize}px;
        height: ${containerSize}px;
      ">
        <!-- Core marker dot -->
        <div class="marker-core"></div>
      </div>
    `;

    return L.divIcon({
      html: iconHtml,
      className: 'custom-marker-icon',
      iconSize: [containerSize, containerSize],
      iconAnchor: [containerSize / 2, containerSize / 2],
    });
  };

  return (
    <Marker
      position={[city.latitude, city.longitude]}
      icon={createCustomIcon()}
      eventHandlers={{
        mouseover: (e) => {
          e.target.openTooltip();
        },
      }}
    >
      <Tooltip
        direction="top"
        offset={[0, -10]}
        opacity={1}
        permanent={false}
      >
        <div className="flex items-center justify-between gap-3 p-2">
          {/* City and Country on the left */}
          <div>
            <p className="mb-0.5 text-sm leading-tight font-medium whitespace-nowrap text-foreground">
              {city.city}
            </p>
            <p className="text-xs leading-tight whitespace-nowrap text-muted-foreground">
              {city.country}
            </p>
          </div>

          {/* User count with icon on the right */}
          <div className="flex shrink-0 items-center gap-1 border-s ps-2">
            <UsersIcon className="size-[13px] shrink-0 text-muted-foreground" />
            <p className="text-sm leading-none font-medium text-foreground">
              {city.uniqueUsers}
            </p>
          </div>
        </div>
      </Tooltip>
    </Marker>
  );
};

// Pick the cities that account for the top 70% of users — these are the
// "dense" markers we want centered. Falls back to all cities if user counts
// are zero/missing.
const computeFocusedCities = (data: CityData[]): CityData[] => {
  if (data.length === 0) return [];
  const sorted = [...data].sort((a, b) => (b.uniqueUsers || 0) - (a.uniqueUsers || 0));
  const totalUsers = sorted.reduce((sum, c) => sum + (c.uniqueUsers || 0), 0);
  if (totalUsers === 0) return sorted;

  const threshold = totalUsers * 0.7;
  let cumulative = 0;
  const focused: CityData[] = [];
  for (const city of sorted) {
    focused.push(city);
    cumulative += city.uniqueUsers || 0;
    if (cumulative >= threshold) break;
  }
  return focused;
};

// User-weighted centroid of the focused cities. This puts the densest
// cluster in the middle of the viewport instead of the geometric center
// of the bounding box.
const computeWeightedCenter = (cities: CityData[]): L.LatLng | null => {
  if (cities.length === 0) return null;
  const totalWeight = cities.reduce((s, c) => s + (c.uniqueUsers || 0), 0);
  if (totalWeight === 0) {
    const lat = cities.reduce((s, c) => s + c.latitude, 0) / cities.length;
    const lng = cities.reduce((s, c) => s + c.longitude, 0) / cities.length;
    return L.latLng(lat, lng);
  }
  const lat = cities.reduce((s, c) => s + c.latitude * (c.uniqueUsers || 0), 0) / totalWeight;
  const lng = cities.reduce((s, c) => s + c.longitude * (c.uniqueUsers || 0), 0) / totalWeight;
  return L.latLng(lat, lng);
};

// Center on the user-weighted centroid, but pick a zoom level that would
// have fit the focus bounds — so we get density-aware centering with a
// sensible "fit-the-cluster" zoom.
const focusMapOnDensity = (
  map: L.Map,
  data: CityData[],
  options: { animate?: boolean; padding?: [number, number] } = {},
) => {
  const focused = computeFocusedCities(data);
  if (focused.length === 0) return;

  const center = computeWeightedCenter(focused);
  if (!center) return;

  const padding = options.padding ?? [20, 20];
  const maxZoom = data.length <= 3 ? 10 : data.length <= 10 ? 8 : 6;

  let targetZoom: number;
  if (focused.length === 1) {
    targetZoom = maxZoom;
  } else {
    const bounds = L.latLngBounds(focused.map(c => [c.latitude, c.longitude] as [number, number]));
    const fitZoom = map.getBoundsZoom(bounds, false, L.point(padding[0], padding[1]));
    targetZoom = Math.min(maxZoom, fitZoom);
  }

  map.setView(center, targetZoom, { animate: options.animate ?? false });
};

// Map controller component to handle zoom and center
const MapController: React.FC<{ data: CityData[] }> = ({ data }) => {
  const map = useMap();

  useEffect(() => {
    if (data.length === 0) return;

    // Initialize smooth wheel zoom
    const mapWithSmoothZoom = map as any;
    if (!mapWithSmoothZoom.smoothWheelZoom) {
      // @ts-ignore
      L.Map.addInitHook('addHandler', 'smoothWheelZoom', (L as any).Handler.SmoothWheelZoom);
    }
    if (mapWithSmoothZoom.smoothWheelZoom) {
      mapWithSmoothZoom.smoothWheelZoom.enable();
    }

    focusMapOnDensity(map, data);
  }, [data, map]);

  return null;
};

// Map controls component - must be inside MapContainer
const MapControls: React.FC<{
  data: CityData[];
  resetTrigger: number;
}> = ({ data, resetTrigger }) => {
  const map = useMap();

  useEffect(() => {
    if (resetTrigger === 0) return;
    if (data.length === 0) return;

    focusMapOnDensity(map, data, { animate: true });
  }, [resetTrigger, data, map]);

  return null;
};

// Fullscreen handler component - must be inside MapContainer
const FullscreenHandler: React.FC<{
  data: CityData[];
  isFullscreen: boolean;
}> = ({ data, isFullscreen }) => {
  const map = useMap();

  useEffect(() => {
    if (data.length === 0) return;

    const timer = setTimeout(() => {
      map.invalidateSize();
      focusMapOnDensity(map, data, { animate: true });
    }, 100);

    return () => clearTimeout(timer);
  }, [isFullscreen, data, map]);

  return null;
};


export const CitiesMap: React.FC<CitiesMapProps> = ({ data, isFullscreen = false, onToggleFullscreen, loading = false }) => {
  const { t } = useTranslation();
  const { mode } = useContext(ColorModeContext);
  const isDarkMode = mode === 'dark';
  const [isLoading, setIsLoading] = useState(true);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [isMounted, setIsMounted] = useState(true);
  const [mapKey, setMapKey] = useState(() => Date.now()); // Unique key to force remount
  const [mapReady, setMapReady] = useState(false); // Delay map render to avoid Strict Mode issues
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Track mount state and delay map render to avoid React Strict Mode double-mount issues
  useEffect(() => {
    setIsMounted(true);
    // Delay map initialization to avoid Strict Mode's double-mount causing container reuse errors
    const timer = setTimeout(() => {
      setMapReady(true);
    }, 0);
    return () => {
      clearTimeout(timer);
      setIsMounted(false);
      setMapReady(false);
    };
  }, []);

  // Force new map key when theme changes to avoid container reuse issues
  useEffect(() => {
    setMapReady(false);
    setMapKey(Date.now());
    // Re-enable map after key change
    const timer = setTimeout(() => {
      setMapReady(true);
    }, 0);
    return () => clearTimeout(timer);
  }, [isDarkMode]);

  useEffect(() => {
    // Brief loading state for map initialization only when not loading from parent
    if (!loading) {
      const timer = setTimeout(() => {
        if (isMounted) setIsLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [loading, isMounted]);

  const handleResetView = () => {
    setResetTrigger(prev => prev + 1);
  };

  // Get appropriate tile layer URL based on theme - using neutral/grayscale colors with labels
  const getTileLayerUrl = () => {
    if (isDarkMode) {
      return 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
    } else {
      return 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
    }
  };

  const maxUsers = Math.max(...data.map(c => c.uniqueUsers), 1);

  // Calculate center point
  const centerPoint = useMemo(() => {
    if (data.length === 0) return { lat: 30, lng: 10 };

    let totalWeight = 0;
    let weightedLat = 0;
    let weightedLng = 0;

    data.forEach(city => {
      const weight = city.uniqueUsers;
      totalWeight += weight;
      weightedLat += city.latitude * weight;
      weightedLng += city.longitude * weight;
    });

    if (totalWeight === 0) return { lat: 30, lng: 10 };

    return {
      lat: weightedLat / totalWeight,
      lng: weightedLng / totalWeight,
    };
  }, [data]);

  // Get country statistics for legend
  const countryStats = useMemo(() => {
    const countryMap = new Map<string, { uniqueUsers: number; cities: CityData[] }>();

    data.forEach(city => {
      if (!countryMap.has(city.country)) {
        countryMap.set(city.country, { uniqueUsers: 0, cities: [] });
      }
      const countryData = countryMap.get(city.country)!;
      countryData.uniqueUsers += city.uniqueUsers;
      countryData.cities.push(city);
    });

    return Array.from(countryMap.entries())
      .map(([country, stats]) => ({
        country,
        uniqueUsers: stats.uniqueUsers,
        cities: stats.cities,
      }))
      .sort((a, b) => b.uniqueUsers - a.uniqueUsers);
  }, [data]);

  if (loading || isLoading) {
    return (
      <div
        className={cn(
          'flex w-full flex-col',
          isFullscreen ? 'fixed inset-0 z-[9999] h-full bg-card' : 'relative h-full bg-transparent',
        )}
      >
        <div
          className={cn(
            'relative isolate w-full overflow-hidden bg-card',
            isFullscreen ? 'h-full' : 'h-[400px]',
          )}
        >
          {/* Skeleton loader for map */}
          <Skeleton className="size-full" />

          {/* Loading message overlay */}
          <div className="absolute top-1/2 left-1/2 z-[1000] -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-card px-6 py-4 text-center">
            <p className="text-sm font-medium text-muted-foreground">
              {t('PAGES.DASHBOARD.LOADING_MAP')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex w-full flex-col',
        isFullscreen ? 'fixed inset-0 z-[9999] h-full bg-card' : 'relative h-full bg-transparent',
      )}
    >
      {/* Map Container. `cities-map` scopes the leaflet chrome rules in index.css. */}
      <div
        ref={containerRef}
        className={cn(
          // `isolate`: the controls below sit at z-1000 to clear leaflet's own
          // panes. Without a stacking context that is measured against the whole
          // page, and they float over dialogs opened elsewhere in the app.
          'cities-map relative isolate w-full overflow-hidden',
          isFullscreen ? 'h-full max-h-full flex-[1_1_100%]' : 'h-[400px] max-h-[400px] flex-[0_0_auto]',
        )}
      >
        {mapReady && (
          <MapContainer
            key={mapKey}
            center={[centerPoint.lat, centerPoint.lng]}
            zoom={data.length <= 3 ? 8 : 4}
            minZoom={3}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
            scrollWheelZoom={false}
            zoomAnimation={true}
            fadeAnimation={true}
            markerZoomAnimation={true}
          >
            {/* Dynamic tile layer - changes based on theme - neutral colors */}
            <TileLayer
              url={getTileLayerUrl()}
              className="map-tiles"
            />

            {/* Render city markers */}
            {data.map((city, index) => (
              <AnimatedCityMarker
                key={`${city.city}-${city.country}`}
                city={city}
                index={index}
              />
            ))}

            {/* Map controller */}
            <MapController data={data} />

            {/* Map controls for reset */}
            <MapControls data={data} resetTrigger={resetTrigger} />

            {/* Fullscreen handler */}
            <FullscreenHandler data={data} isFullscreen={isFullscreen} />
          </MapContainer>
        )}

        {/* No data overlay */}
        {data.length === 0 && (
          <div className="absolute top-1/2 left-1/2 z-[1000] -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-card px-6 py-4 text-center">
            <p className="text-sm font-medium text-muted-foreground">
              {t('PAGES.DASHBOARD.NO_CITY_DATA_AVAILABLE')}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t('PAGES.DASHBOARD.CITY_DATA_WILL_APPEAR')}
            </p>
          </div>
        )}

        {/* Control Buttons */}
        <div className="absolute end-2.5 top-2.5 z-[1000] flex flex-col gap-1">
          {onToggleFullscreen && (
            <Button
              variant="outline"
              size="icon-sm"
              onClick={onToggleFullscreen}
              aria-label={t('COMMON.FULLSCREEN', 'Fullscreen') as string}
              className="bg-card backdrop-blur-md"
            >
              {isFullscreen ? <Minimize2 /> : <Maximize2 />}
            </Button>
          )}
          <Button
            variant="outline"
            size="icon-sm"
            onClick={handleResetView}
            aria-label={t('COMMON.RESET_VIEW', 'Reset view') as string}
            className="bg-card backdrop-blur-md"
          >
            <LocateFixed />
          </Button>
        </div>
      </div>
    </div>
  );
};
